// src/components/antenna/antenna-business-metrics.tsx
import React from "react";
import { ChevronDown, ChevronUp, Box } from "lucide-react";
import { Card } from "../ui/card";
import { MetricTerm } from "../metric-term";
import { MetricInsightChips } from "../metric-insight-chips";
import { ModelFilterTabs } from "../model-filter-tabs";
import { metricExplanations, heroMetricsInsights } from "../../data/antenna-metric-explanations";
import { normalizeProviderKey } from "@/app/constants/models";
import { signalLabels } from "@/app/utils/signal-items";

// ─── Types ────────────────────────────────────────────────────────────────────

interface HeroMetricItem {
  percentage: number;
  calculation: string;
}

interface HeroMetrics {
  referralConversations:         HeroMetricItem;
  bestInCategory:                HeroMetricItem;
  numberOneRecommendation:       HeroMetricItem;
  sentimentSnapshot?:            HeroMetricItem;
  categoryLeaderRecommendation?: HeroMetricItem;
  categoryLeaderName?:           string;
}

interface DataIntegrityIssue {
  type:     string;
  count:    number;
  severity: string;
  color?:   string;
  bgColor?: string;
}

interface DataIntegritySummary {
  issues:         DataIntegrityIssue[];
  modelsAffected: string[];
  totalModels:    number;
}

interface AntennaBusinessMetricsProps {
  heroMetrics: HeroMetrics;
  heroMetricsByModel?: Record<string, HeroMetrics>;
  perceptionData?: any;
  perceptionDataByModel?: Record<string, any>;
  themes?: any[];
  themesByModel?: Record<string, any>;
  dataIntegrity?: DataIntegritySummary;
  modelTabs?: { value: string; label: string }[];
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  isExpanded: boolean;
  toggleExpanded: () => void;
  onOpenAIChat: (msg: string) => void;
  category?: string;
  brandName?: string;
  onNavigate?: (page: string) => void;
}

const EMPTY_METRIC: HeroMetricItem = { percentage: 0, calculation: "" };

// ─── Small shared pieces ───────────────────────────────────────────────────

function ModelSourceBadge({ label }: { label: string }) {
  return (
    <span
      style={{
        display:         "inline-flex",
        alignItems:      "center",
        padding:         "2px 8px",
        borderRadius:    9999,
        backgroundColor: "var(--alpha-teal-10)",
        border:          "1px solid var(--alpha-teal-20)",
        color:           "var(--alpha-teal)",
        fontSize:        "var(--text-xs)",
        fontWeight:      600,
        marginLeft:      6,
      }}
    >
      {label}
    </span>
  );
}

function CardShell({ children }: { children: React.ReactNode }) {
  // No h-full: height:100% inside an auto-sized grid row resolves to content
  // height and defeats the grid's default equal-height stretch, leaving the
  // three cards with ragged bottoms. Grid stretch + the mt-auto on
  // CalculationDetails give every card the same height with the footer link
  // pinned to the same baseline.
  return (
    <div
      className="p-4 rounded-xl flex flex-col"
      style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
    >
      {children}
    </div>
  );
}

function CardTitle({ defKey, onAIChat, children }: { defKey: string; onAIChat: (msg: string) => void; children: React.ReactNode }) {
  return (
    <h4 className="mb-3" style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--text-base)" }}>
      <MetricTerm {...metricExplanations[defKey]} onOpenAIChat={() => onAIChat(defKey)}>
        {children}
      </MetricTerm>
    </h4>
  );
}

// A muted "nothing to show yet" block, used in place of StatBlock/the bar
// chart when the underlying scan has no data for this metric (e.g. an older
// scan that ran before this metric existed, or a model that returned no
// themes to score sentiment from). Showing a real 0% or a blank name here
// would read as a bad/alarming result instead of "data not collected yet".
function EmptyStatBlock({ message }: { message: React.ReactNode }) {
  return (
    <div
      style={{
        position:        "relative",
        backgroundColor: "var(--card)",
        border:          "1px dashed var(--border)",
        borderRadius:    10,
        padding:         "18px",
        marginBottom:    12,
        display:         "flex",
        alignItems:      "center",
        justifyContent:  "center",
        flexGrow:        1,
        minHeight:       "104px",
        textAlign:       "center",
      }}
    >
      <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", lineHeight: 1.4 }}>
        {message}
      </p>
    </div>
  );
}

// Deep Signal-style single-number stat block: bold accent-colored value, a
// plain-language description, and a small status badge — matches the
// "funnel stat card" pattern used on the Deep Signal page (top accent bar +
// big number) so both modules read as one visual system.
function StatBlock({
  value,
  description,
  badgeLabel,
  accentColor,
  onClick,
}: {
  value: string;
  description: React.ReactNode;
  badgeLabel: string;
  accentColor: string;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        position:        "relative",
        backgroundColor: "var(--card)",
        border:          "1px solid var(--border)",
        borderRadius:    10,
        overflow:        "hidden",
        padding:          "18px",
        cursor:          "pointer",
        marginBottom:    12,
        flexGrow:        1,
      }}
    >
      <div style={{ height: 3, backgroundColor: accentColor, position: "absolute", top: 0, left: 0, right: 0 }} />
      <p style={{ fontSize: "var(--text-3xl)", fontWeight: 800, color: accentColor, marginBottom: 6, marginTop: 4 }}>
        {value}
      </p>
      <p style={{ fontSize: "var(--text-sm)", fontWeight: 400, color: "var(--foreground)", marginBottom: 10, lineHeight: 1.4 }}>
        {description}
      </p>
      <span
        style={{
          display:         "inline-block",
          fontSize:        10,
          fontWeight:      500,
          color:           "var(--muted-foreground)",
          backgroundColor: "var(--muted)",
          borderRadius:    9999,
          padding:         "2px 8px",
        }}
      >
        {badgeLabel}
      </span>
    </div>
  );
}

function CalculationDetails({ children }: { children: React.ReactNode }) {
  return (
    <details className="group mt-auto">
      <summary
        className="cursor-pointer"
        style={{ color: "var(--accent)", fontSize: "var(--text-xs)", fontWeight: "var(--font-weight-medium)" }}
      >
        <span className="group-hover:underline">How is this calculated?</span>
      </summary>
      <div
        className="mt-3 p-3 rounded-lg border"
        style={{
          backgroundColor: "var(--card)",
          borderColor:     "var(--border)",
          fontSize:        "var(--text-xs)",
          color:           "var(--muted-foreground)",
        }}
      >
        {children}
      </div>
    </details>
  );
}

// ─── Card 1: Brand Asset / Liability ────────────────────────────────────────

// Take themed attribute labels first (already ranked by how loudly AI emphasizes
// them), then fold in any bare strengths/weaknesses AI volunteered without a
// scored theme so the list still fills to three. De-dupe case-insensitively.
function topThreeLabels(themed: string[], fallback: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    const label = (raw || "").trim();
    const key = label.toLowerCase();
    if (!label || seen.has(key)) return;
    seen.add(key);
    out.push(label);
  };
  themed.forEach(push);
  (fallback || []).forEach(push);
  return out.slice(0, 3);
}

// Items longer than this are treated as sentence-length (the model sometimes
// returns full explanations instead of short labels) and get clamped to two
// lines until the column is expanded. Roughly two lines at text-xs in this
// column width.
const ASSET_ITEM_CLAMP_CHARS = 70;

function AssetLiabilityColumn({
  title,
  items,
  accent,
}: {
  title: string;
  items: string[];
  accent: string;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const hasLongItems = items.some((label) => (label || "").length > ASSET_ITEM_CLAMP_CHARS);

  const itemList = (clamp: boolean) => (
    <ul className="space-y-1.5" style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {items.map((label, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accent }} />
          <span
            title={clamp && (label || "").length > ASSET_ITEM_CLAMP_CHARS ? label : undefined}
            style={{
              fontSize: "var(--text-xs)",
              color: "var(--foreground)",
              lineHeight: 1.4,
              fontWeight: 400,
              ...(clamp
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical" as const,
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {label}
          </span>
        </li>
      ))}
    </ul>
  );

  const columnHeader = (
    <p
      style={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: accent,
        marginBottom: 8,
      }}
    >
      {title}
    </p>
  );

  return (
    // No h-full here: height:100% inside an auto-sized grid row resolves to
    // content height and DEFEATS the grid's default equal-height stretch,
    // which is exactly the uneven-boxes bug. Let the grid stretch both
    // columns to the taller one.
    //
    // Expansion is an OVERLAY, not in-flow growth: growing in place would
    // stretch the equal-height row and inflate every card in it. The
    // expanded column floats above the layout instead, so nothing else
    // moves.
    <div
      className="rounded-lg p-3 flex flex-col"
      style={{ position: "relative", backgroundColor: "var(--card)", border: "1px solid var(--border)", borderTop: `3px solid ${accent}` }}
    >
      {columnHeader}
      {items.length === 0 ? (
        <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>No data yet.</p>
      ) : (
        itemList(true)
      )}
      {hasLongItems && (
        <button
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1 mt-auto"
          style={{ fontSize: "var(--text-xs)", color: "#28a9d2", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, paddingTop: 8 }}
        >
          <ChevronDown className="w-3 h-3" /> Show more
        </button>
      )}
      {expanded && (
        <div
          className="rounded-lg p-3 flex flex-col"
          style={{
            position: "absolute",
            top: -1,
            left: -1,
            right: -1,
            minHeight: "calc(100% + 2px)",
            zIndex: 30,
            backgroundColor: "var(--card)",
            border: "1px solid var(--border)",
            borderTop: `3px solid ${accent}`,
            boxShadow: "0 12px 28px rgba(0,0,0,0.14)",
          }}
        >
          {columnHeader}
          {itemList(false)}
          <button
            onClick={() => setExpanded(false)}
            className="flex items-center gap-1"
            style={{ fontSize: "var(--text-xs)", color: "#28a9d2", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, paddingTop: 8 }}
          >
            <ChevronUp className="w-3 h-3" /> Show less
          </button>
        </div>
      )}
    </div>
  );
}

function BrandAssetLiabilityCard({
  assets,
  liabilities,
  titleLabel,
  brandLabel,
  onAIChat,
}: {
  assets: string[];
  liabilities: string[];
  titleLabel: string;
  brandLabel: string;
  onAIChat: (msg: string) => void;
}) {
  const hasNoData = assets.length === 0 && liabilities.length === 0;
  return (
    <CardShell>
      <h4 className="mb-3" style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--text-base)" }}>
        <MetricTerm
          definition={`The attributes AI praises most loudly about ${brandLabel} (assets) and the ones it knocks most loudly (liabilities).`}
          calculation="Drawn from the attribute themes in this scan, ranked by how much weight AI gives each. The top three positives are your assets; the top three negatives are your liabilities."
          interpretation="Assets are the messages doing your selling for free; liabilities are the objections AI plants before a buyer ever reaches you. Reinforce the assets, and get ahead of the liabilities in your content."
          onOpenAIChat={() => onAIChat("AI Brand Anchors")}
        >
          {titleLabel}
        </MetricTerm>
      </h4>
      {hasNoData ? (
        <EmptyStatBlock message="No asset or liability data yet. Run a new scan to populate this." />
      ) : (
        <div className="grid grid-cols-2 gap-3 flex-1" style={{ marginBottom: 12 }}>
          <AssetLiabilityColumn title="Assets" items={assets} accent="var(--alpha-teal)" />
          <AssetLiabilityColumn title="Liabilities" items={liabilities} accent="var(--infrared-coral)" />
        </div>
      )}
      <CalculationDetails>
        Pulled from the attribute themes AI surfaced in this scan, ranked by how much weight AI gives each. The three most heavily emphasized positive themes become your assets; the three most heavily emphasized negative themes become your liabilities. Where a side has fewer than three scored themes, additional strengths or weaknesses AI named are folded in.
      </CalculationDetails>
    </CardShell>
  );
}

// ─── Card 2: Recommend You vs. Category Leader ──────────────────────────────

const BAR_CHART_MAX_HEIGHT = 96;
// Raised from 4px so a small-but-real percentage (e.g. 1.3%) still renders as
// a visible bar instead of a near-invisible sliver next to the leader's bar.
const BAR_CHART_MIN_HEIGHT = 14;

function VerticalBar({
  percentage,
  label,
  value,
  color,
}: {
  percentage: number;
  label: string;
  value: string;
  color: string;
}) {
  const barHeight = Math.max(BAR_CHART_MIN_HEIGHT, (Math.max(0, Math.min(100, percentage)) / 100) * BAR_CHART_MAX_HEIGHT);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, minWidth: 0 }}>
      <span style={{ fontSize: "var(--text-base)", fontWeight: 700, color, marginBottom: 6 }}>{value}</span>
      <div
        style={{
          width:        "44px",
          height:       `${barHeight}px`,
          backgroundColor: color,
          borderRadius: "6px 6px 0 0",
          transition:   "height 0.3s ease",
        }}
      />
      <span
        style={{
          marginTop:    8,
          fontSize:     "var(--text-xs)",
          fontWeight:   500,
          color:        "var(--muted-foreground)",
          textAlign:    "center",
          maxWidth:     "100%",
          overflow:     "hidden",
          textOverflow: "ellipsis",
          whiteSpace:   "nowrap",
        }}
      >
        {label}
      </span>
    </div>
  );
}

// Sits between the two bars and names the gap directly (e.g. "32.0 pt gap")
// instead of leaving the viewer to subtract two stark percentages themselves.
function GapIndicator({ gap, youAhead }: { gap: number; youAhead: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: 8 }}>
      <span style={{ fontSize: "var(--text-base)", fontWeight: 800, color: "var(--muted-foreground)" }}>
        {youAhead ? "+" : "-"}{gap.toFixed(1)}
      </span>
      <span
        style={{
          fontSize:      10,
          fontWeight:    500,
          color:         "var(--muted-foreground)",
          textTransform: "uppercase",
          letterSpacing: "0.03em",
          whiteSpace:    "nowrap",
        }}
      >
        pt gap
      </span>
    </div>
  );
}

function LeaderComparisonCard({
  you,
  leader,
  brandLabel,
  leaderName,
  onAIChat,
}: {
  you: HeroMetricItem;
  leader: HeroMetricItem;
  brandLabel: string;
  leaderName: string;
  onAIChat: (msg: string) => void;
}) {
  // The leader's name and percentage come from the brand's competitive
  // position ranking and from a separate per-model field, both of which can
  // be empty on an older scan that predates this metric. Don't fabricate a
  // "the category leader" comparison against 0% — that reads as a real (and
  // wrong) result. Show an explicit empty state instead.
  const hasLeaderData = leaderName.trim().length > 0;
  const leaderLabel = leaderName.trim();
  const gap = Math.abs(leader.percentage - you.percentage);
  const youAhead = you.percentage > leader.percentage;
  return (
    <CardShell>
      <CardTitle defKey="Recommend You vs. Category Leader" onAIChat={onAIChat}>
        Recommend You vs. Category Leader
      </CardTitle>
      {hasLeaderData ? (
        <div
          className="mb-3 flex-1"
          style={{
            display:         "flex",
            flexDirection:   "column",
            justifyContent:  "center",
            backgroundColor: "var(--card)",
            border:          "1px solid var(--border)",
            borderRadius:    10,
            cursor:          "pointer",
          }}
          onClick={() =>
            onAIChat(
              `${brandLabel} is recommended ${you.percentage}% of the time vs. ${leaderLabel} at ${leader.percentage}%. How is this measured?`,
            )
          }
        >
          <p
            style={{
              fontSize:   "var(--text-sm)",
              fontWeight: "var(--font-weight-medium)",
              color:      "var(--foreground)",
              textAlign:  "center",
              lineHeight: 1.35,
              padding:    "14px 20px 2px",
              margin:     0,
            }}
          >
            How often AI names each as the single best option
          </p>
          <div
            style={{
              display:        "flex",
              alignItems:     "flex-end",
              justifyContent: "center",
              gap:            "16px",
              padding:        "14px 12px 14px",
            }}
          >
            <VerticalBar percentage={you.percentage} value={`${you.percentage}%`} label={brandLabel} color="var(--signal-blue)" />
            <GapIndicator gap={gap} youAhead={youAhead} />
            <VerticalBar percentage={leader.percentage} value={`${leader.percentage}%`} label={leaderLabel} color="var(--alpha-teal)" />
          </div>
        </div>
      ) : (
        <EmptyStatBlock message="Category leader not yet identified — run a new scan to populate this comparison." />
      )}
      <CalculationDetails>
        <p style={{ marginBottom: "8px" }}>
          <strong>{brandLabel}:</strong> {you.calculation || "Calculation not available for this model."}
        </p>
        <p>
          <strong>{hasLeaderData ? leaderLabel : "Category leader"}:</strong> {leader.calculation || "Calculation not available for this model."}
        </p>
      </CalculationDetails>
    </CardShell>
  );
}

// ─── Card 3: Flagged Factual Gaps ───────────────────────────────────────────

function FlaggedFactualGapsCard({
  dataIntegrity,
  brandLabel,
  onAIChat,
}: {
  dataIntegrity: DataIntegritySummary;
  brandLabel: string;
  onAIChat: (msg: string) => void;
}) {
  const issues = dataIntegrity.issues || [];
  const total = issues.reduce((sum, issue) => sum + Number(issue?.count || 0), 0);
  const accentColor = total > 0 ? "var(--infrared-coral)" : "var(--alpha-teal)";
  const badgeLabel = total > 0 ? "Needs attention" : "Clean";

  return (
    <CardShell>
      <CardTitle defKey="Flagged Factual Gaps" onAIChat={onAIChat}>Flagged Factual Gaps</CardTitle>
      <StatBlock
        value={`${total}`}
        description={<>incorrect, missing, or outdated facts AI has about <strong>{brandLabel}</strong></>}
        badgeLabel={badgeLabel}
        accentColor={accentColor}
        onClick={() => onAIChat(`Flagged Factual Gaps: ${total}. Help me fix these.`)}
      />
      <CalculationDetails>
        {issues.length === 0 && <p>No factual issues detected across tracked models.</p>}
        {issues.map((issue, i) => (
          <p key={i} style={{ marginBottom: i < issues.length - 1 ? "4px" : 0 }}>
            <span style={{ color: issue.color || "var(--foreground)", fontWeight: 600 }}>{issue.type}:</span>{" "}
            {issue.count} ({issue.severity} severity)
          </p>
        ))}
      </CalculationDetails>
    </CardShell>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AntennaBusinessMetrics({
  heroMetrics,
  heroMetricsByModel = {},
  perceptionData = {},
  perceptionDataByModel = {},
  themes = [],
  themesByModel = {},
  dataIntegrity,
  modelTabs = [],
  selectedModel = "all",
  onModelChange,
  isExpanded,
  toggleExpanded,
  onOpenAIChat,
  category,
  brandName,
  onNavigate,
}: AntennaBusinessMetricsProps) {
  const isAll = selectedModel === "all";
  const brandLabel = (brandName || "").trim() || "your brand";

  // heroMetricsByModel comes from the backend keyed by its own normalized
  // provider id. Re-key it here through the frontend's normalizer too, so a
  // lookup never silently misses (and falls back to the all-models average)
  // just because the two sides' key casing/format don't line up exactly.
  const normalizedMetricsByModel = React.useMemo(() => {
    const out: Record<string, HeroMetrics> = {};
    for (const [rawKey, value] of Object.entries(heroMetricsByModel)) {
      out[normalizeProviderKey(rawKey)] = value as HeroMetrics;
    }
    return out;
  }, [heroMetricsByModel]);

  const activeMetrics: HeroMetrics = React.useMemo(() => {
    if (isAll) return heroMetrics;
    const key = normalizeProviderKey(selectedModel);
    return normalizedMetricsByModel[key] ?? heroMetrics;
  }, [isAll, selectedModel, normalizedMetricsByModel, heroMetrics]);

  // AI Brand Anchors: top 3 attributes AI praises vs knocks, drawn from
  // the same detected-theme source as the Brand Associations card so the two stay
  // consistent. Themes carry an impactScore (how loudly AI emphasizes them) and a
  // sentiment; we rank by that and fall back to bare strengths/weaknesses to fill.
  const { assets, liabilities } = React.useMemo(() => {
    const key = normalizeProviderKey(selectedModel);
    const activeThemes: any[] = isAll
      ? themes
      : (themesByModel[key]?.detectedThemes ?? themes);
    const activePerception: any = isAll
      ? perceptionData
      : (perceptionDataByModel[key] ?? perceptionData);

    const themedLabels = (sentiment: "positive" | "negative"): string[] =>
      (activeThemes || [])
        .filter((t) => t?.sentiment === sentiment)
        .sort((a, b) => (b?.impactScore ?? 0) - (a?.impactScore ?? 0))
        .map((t) => t?.theme)
        .filter(Boolean);

    return {
      // signalLabels adapts structured {label, description} entries and
      // legacy bare strings alike down to display labels.
      assets: topThreeLabels(themedLabels("positive"), signalLabels(activePerception?.strengths)),
      liabilities: topThreeLabels(themedLabels("negative"), signalLabels(activePerception?.weaknesses)),
    };
  }, [isAll, selectedModel, themes, themesByModel, perceptionData, perceptionDataByModel]);

  // The category leader's identity doesn't change with the model filter —
  // it's sourced once from Competitive Position, not per-model business_metrics.
  const leaderName = heroMetrics.categoryLeaderName || "";

  const modelLabel = isAll
    ? null
    : (modelTabs.find((t) => t.value === selectedModel)?.label ?? selectedModel);

  const hasPerModelData =
    Object.keys(heroMetricsByModel).length > 0 && modelTabs.length > 0;

  const trimmedCategory = (category || "").trim();

  const safeDataIntegrity: DataIntegritySummary =
    dataIntegrity ?? { issues: [], modelsAffected: [], totalModels: 0 };

  return (
    <Card style={{ backgroundColor: "var(--card)", borderColor: "var(--border)" }}>
      <div className="p-6 space-y-8">

        {/* ── Section header ─────────────────────────────────────────── */}
        <div
          className="cursor-pointer"
          onClick={toggleExpanded}
          style={{ userSelect: "none" }}
        >
          <div className="flex items-center justify-between gap-4">
            <h3 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)" }}>
              Business Metrics Overview
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isAll && modelLabel && <ModelSourceBadge label={modelLabel} />}
              {hasPerModelData && (
                <ModelFilterTabs
                  models={modelTabs}
                  selectedModel={selectedModel}
                  onModelChange={onModelChange ?? (() => {})}
                  combinedLabel="All Models"
                  includeCombined={true}
                />
              )}
              {isExpanded
                ? <ChevronUp   className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
                : <ChevronDown className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />}
            </div>
          </div>
          <p style={{ color: "var(--muted-foreground)", marginTop: "4px", fontSize: "var(--text-sm)" }}>
            {isAll
              ? "How does model behavior shape business outcomes?"
              : `${modelLabel}'s AI-behavior estimates — switch to "All Models" for the averaged view`}
          </p>
          {trimmedCategory && (
            <div
              title="Brand's primary category — sourced from competitive position analysis."
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                marginTop: 7, fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
              }}
            >
              <Box className="w-3.5 h-3.5" style={{ opacity: 0.7 }} />
              <span style={{ fontWeight: 500, color: "var(--foreground)" }}>Category</span>
              <span style={{ opacity: 0.5 }}>·</span>
              {trimmedCategory}
            </div>
          )}
        </div>

        {isExpanded && (
          <div className="@container">
            <div className="grid grid-cols-1 @2xl:grid-cols-3 gap-8">
              <BrandAssetLiabilityCard
                assets={assets}
                liabilities={liabilities}
                titleLabel={isAll ? "AI Brand Anchors" : `${modelLabel}'s Brand Anchors`}
                brandLabel={brandLabel}
                onAIChat={onOpenAIChat}
              />
              <LeaderComparisonCard
                you={activeMetrics.numberOneRecommendation}
                leader={activeMetrics.categoryLeaderRecommendation ?? EMPTY_METRIC}
                brandLabel={brandLabel}
                leaderName={leaderName}
                onAIChat={onOpenAIChat}
              />
              <FlaggedFactualGapsCard
                dataIntegrity={safeDataIntegrity}
                brandLabel={brandLabel}
                onAIChat={onOpenAIChat}
              />
            </div>

            {/* ── Bridge to Deep Signal — one direct bar, caveat folded in ── */}
            <div
              className="mt-6 p-4 rounded-xl flex flex-col items-end gap-3"
              style={{
                backgroundColor: "var(--alpha-teal-5)",
                border:          "1px solid var(--alpha-teal-20)",
              }}
            >
              <p style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: "1.5", width: "100%" }}>
                This is a directional read from self-reported AI estimates of how it would recommend your brand.{" "}
                <strong>Deep Signal</strong> delivers statistically significant analysis of how AI reasoning becomes real recommendations.
              </p>
              <button
                onClick={() => onNavigate?.("deep-signal")}
                className="flex-shrink-0"
                style={{
                  padding:         "5px 12px",
                  borderRadius:    6,
                  backgroundColor: "var(--alpha-teal)",
                  color:           "white",
                  fontSize:        "var(--text-xs)",
                  fontWeight:      600,
                  border:          "none",
                  cursor:          onNavigate ? "pointer" : "default",
                }}
              >
                See your real AI funnel →
              </button>
            </div>

            <div className="pt-6 mt-6 border-t" style={{ borderColor: "var(--border)" }}>
              <MetricInsightChips
                insights={heroMetricsInsights}
                onOpenAIChat={() => onOpenAIChat("Hero Metrics strategy")}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
